import * as React from 'react';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import { RootStore, withInjectedRootStore } from 'providers';
import { usePageModeContext } from 'providers/PageMode/PageMode.provider';
import { ErrorBoundary } from '../ErrorBoundaries/ErrorBoundary';
import { Notification } from '../Notification/Notification';
import { GlobalStyles } from '../App/GlobalStyles';
import { AppLayout } from './AppLayout';
import { BreakpointProvider } from 'providers/BreakpointProvider';
import { StickyHeaderHeightProvider } from 'providers/StickyHeaderHeightProvider';
import { Footer } from '../Footer/Footer';
import { Header } from './Header/Header';
import { useAppLoaded } from '../../hooks/useAppLoaded';
import appCss from '../App/App.scss';
import sunriseCss from './SunriseApp.scss';
import { ScrollRef } from '../ScrollRef/ScrollRef';
import { EnterpriseModalContextProvider } from 'providers/EnterpriseModalContextProvider';
import { ProductsGridRefContextProvider } from 'providers/ProductsGridContextRefProvider/ProductsGridContextRefProvider';
import { Experiments, PurchaseMode } from 'enums';

export interface SunriseAppProps {
  rootStore: RootStore;
}

export const SunriseAppComponent: React.FC<SunriseAppProps> = ({
  rootStore: { appStore },
}) => {
  const { theme, purchaseMode, isChangePlan } = usePageModeContext();
  useAppLoaded(appStore);

  const isReplaceChangePlanPurchaseModeEnabled =
    appStore.experiments[Experiments.ReplaceChangePlanPurchaseMode] === 'true';
  const isUnifiedChangePlanPhase1Enabled =
    appStore.experiments[Experiments.UnifiedChangePlanPhase1] === 'true';

  const purchaseModeClassName =
    isChangePlan &&
      isReplaceChangePlanPurchaseModeEnabled &&
      !isUnifiedChangePlanPhase1Enabled
      ? sunriseCss[PurchaseMode.CHANGE_PLAN]
      : sunriseCss[purchaseMode];

  return (
    <>
      <GlobalStyles />
      <>
        <BreakpointProvider>
          <StickyHeaderHeightProvider>
            <ScrollRef />
            <Header />
            <ErrorBoundary>
              <EnterpriseModalContextProvider>
                <ProductsGridRefContextProvider>
                  <div
                    className={classNames(
                      appCss.mainContent,
                      sunriseCss.container,
                      theme,
                      purchaseModeClassName,
                      {
                        [sunriseCss.anonymous]: appStore.isAnonymous,
                      },
                    )}
                  >
                    <Notification />
                    <AppLayout />
                    <Footer />
                  </div>
                </ProductsGridRefContextProvider>
              </EnterpriseModalContextProvider>
            </ErrorBoundary>
          </StickyHeaderHeightProvider>
        </BreakpointProvider>
      </>
    </>
  );
};

export const SunriseApp = withInjectedRootStore(observer(SunriseAppComponent));
